// utils/loanService.js
import axios from "axios";
import { canManageLoans } from "./permissions";

const API_BASE = "https://zedlenders.pickmesms.com/items";

export const LoanService = {
  /**
   * Delete a loan and handle all cascade deletions with optional refund to selected account
   * @param {string} loanId - The ID of the loan to delete
   * @param {string} token - Auth token
   * @param {string} companyId - Company ID for security
   * @param {string|null} refundAccountId - Optional account ID to refund the loan amount to
   * @returns {Promise<{success: boolean, message: string, details: object}>}
   */
  deleteLoan: async (loanId, token, companyId, refundAccountId = null) => {
    try {
      // 1. Authorization check - only admins can delete loans
      const canManage = await canManageLoans();
      const userRole = localStorage.getItem('userRole');
      const isAdmin = userRole === "Administrator" || userRole === "admin" || userRole === "186a1cb5-7b41-4b0e-b9eb-187cb5de75b1";
      
      // Administrators can always delete loans
      if (!isAdmin && !canManage) {
        throw new Error('Insufficient permissions. Only administrators can delete loans.');
      }

      // 2. Fetch the loan with all related data
      const loanResponse = await axios.get(
        `${API_BASE}/loans/${loanId}?fields=*,transactions.*,collateral.*,loan_commodity_details.*`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const loan = loanResponse.data.data;
      
      // Security check - ensure loan belongs to the company
      if (loan.company !== companyId) {
        throw new Error('Access denied. Loan does not belong to your company.');
      }

      // 3. Collect all related transactions for balance reversion
      const transactionsResponse = await axios.get(
        `${API_BASE}/transactions?filter[loan]=${loanId}&fields=*`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const loanTransactions = transactionsResponse.data.data || [];
      
      // Debug logging
      console.log('🔍 Loan deletion - Found transactions:', loanTransactions.length);
      console.log('🔍 Transaction details:', loanTransactions);

      // 4. Prepare refund logic
      console.log('🔍 Loan deletion - Refund account selected:', refundAccountId || 'None (no refund)');
      console.log('🔍 Loan amount to refund:', refundAccountId ? loan.amount : 'N/A (no refund)');

      // 5. Begin cascade deletion process
      const deletionResults = {
        transactions: 0,
        commodityDetails: 0,
        debts: 0,
        collateralUpdated: false,
        accountsReverted: 0,
        loan: false
      };

      // 5a. Delete loan commodity details if this is a commodity loan
      if (loan.is_commodity_loan) {
        const commodityDetailsResponse = await axios.get(
          `${API_BASE}/loan_commodity_details?filter[loan_id]=${loanId}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        const commodityDetails = commodityDetailsResponse.data.data || [];
        
        for (const detail of commodityDetails) {
          await axios.delete(`${API_BASE}/loan_commodity_details/${detail.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          deletionResults.commodityDetails++;
        }
      }

      // 5b. Delete all loan transactions
      for (const transaction of loanTransactions) {
        await axios.delete(`${API_BASE}/transactions/${transaction.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        deletionResults.transactions++;
      }

      // 5c. Delete debt records
      const debtsResponse = await axios.get(
        `${API_BASE}/debts?filter[loan_id]=${loanId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const debts = debtsResponse.data.data || [];
      for (const debt of debts) {
        await axios.delete(`${API_BASE}/debts/${debt.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        deletionResults.debts++;
      }

      // 5d. Update collateral status to "Released" and clear loan reference
      if (loan.collateral) {
        try {
          const collateralId = typeof loan.collateral === 'object' ? loan.collateral.id : loan.collateral;
          
          console.log('🔍 Updating collateral:', { collateralId, fullCollateral: loan.collateral });
          
          if (collateralId) {
            await axios.patch(
              `${API_BASE}/collateral_register/${collateralId}`,
              {
                status: "Released",
                loan: null
              },
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );
            deletionResults.collateralUpdated = true;
            console.log('✅ Collateral updated successfully');
          }
        } catch (collateralError) {
          console.error('❌ Collateral update failed:', collateralError);
          // Continue with deletion even if collateral update fails
        }
      }

      // 6. Refund loan amount to selected account (if account is provided)
      const loanAmount = parseFloat(loan.amount || 0);
      
      if (refundAccountId && loanAmount > 0) {
        // Get current account balance
        const accountResponse = await axios.get(
          `${API_BASE}/accounts/${refundAccountId}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        const currentBalance = parseFloat(accountResponse.data.data.balance || 0);
        const newBalance = currentBalance + loanAmount;

        // Update account balance
        await axios.patch(
          `${API_BASE}/accounts/${refundAccountId}`,
          { balance: newBalance },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        // Create a transaction record for the refund
        await axios.post(
          `${API_BASE}/transactions`,
          {
            amount: loanAmount,
            transaction_date: new Date().toISOString().split("T")[0],
            company: companyId,
            account: refundAccountId,
            is_loan_transaction: false,
            transaction_status: "Completed",
            transaction_type: "Loan Deletion Refund",
            notes: `Refund of loan amount due to loan deletion (Loan ID: ${loanId}). Original amount: ZMW ${loanAmount.toLocaleString()}`,
            payment_method: "internal_adjustment",
            reference_number: `LOAN-DELETE-REFUND-${loanId}-${Date.now()}`,
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        deletionResults.accountsReverted = 1;
        console.log(`✅ Refunded ZMW ${loanAmount.toLocaleString()} to account ${refundAccountId}`);
      } else if (!refundAccountId) {
        console.log(`ℹ️  No refund account provided - loan amount of ZMW ${loanAmount.toLocaleString()} not refunded`);
        deletionResults.accountsReverted = 0;
      }

      // 7. Finally, delete the loan itself
      await axios.delete(`${API_BASE}/loans/${loanId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      deletionResults.loan = true;

      const refundMessage = refundAccountId 
        ? ` and ZMW ${loanAmount.toLocaleString()} has been refunded to the selected account`
        : ` (no refund processed)`;
      
      return {
        success: true,
        message: `Loan deleted successfully. All related data has been removed${refundMessage}.`,
        details: deletionResults
      };

    } catch (error) {
      console.error("Loan deletion error:", error);
      
      // Return structured error response
      return {
        success: false,
        message: error.response?.data?.errors?.[0]?.message || error.message || "Failed to delete loan",
        details: {
          error: error.response?.data || error.message,
          httpStatus: error.response?.status
        }
      };
    }
  },

  /**
   * Get loan details with related entities for preview before deletion
   * @param {string} loanId - The ID of the loan
   * @param {string} token - Auth token
   * @returns {Promise<object>} Loan details with counts of related entities
   */
  getLoanDeletionPreview: async (loanId, token) => {
    try {
      // Get loan details
      const loanResponse = await axios.get(
        `${API_BASE}/loans/${loanId}?fields=*,borrower.first_name,borrower.last_name,loan_product.loan_name,collateral.description`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const loan = loanResponse.data.data;

      // Count related transactions
      const transactionsResponse = await axios.get(
        `${API_BASE}/transactions?filter[loan]=${loanId}&fields=*`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const transactions = transactionsResponse.data.data || [];
      
      // Debug logging for preview
      console.log('🔍 PREVIEW - Transactions found:', transactions.length);
      console.log('🔍 PREVIEW - Transaction data:', transactions);

      // Count commodity details if applicable
      let commodityDetailsCount = 0;
      if (loan.is_commodity_loan) {
        const commodityResponse = await axios.get(
          `${API_BASE}/loan_commodity_details?filter[loan_id]=${loanId}&fields=id`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        commodityDetailsCount = commodityResponse.data.data?.length || 0;
      }

      // Count debt records
      const debtsResponse = await axios.get(
        `${API_BASE}/debts?filter[loan_id]=${loanId}&fields=id`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const debts = debtsResponse.data.data || [];

      // Calculate detailed balance impact - ONLY actual money movements, not interest
      const accountBalanceChanges = {};
      let totalBalanceImpact = 0;
      
      // Debug logging to see what transactions we have
      console.log('🔍 Loan Deletion Preview - Transactions:', transactions);
      
      for (const transaction of transactions) {
        const { transaction_type, amount, from_account, to_account, account } = transaction;
        const txAmount = parseFloat(amount || 0);
        
        // Debug each transaction with ALL fields
        console.log('🔍 Processing transaction:', {
          id: transaction.id,
          transaction_type,
          amount,
          from_account,
          to_account,
          account,
          txAmount,
          fullTransaction: transaction // Show the complete transaction object
        });
        
        // Only process transactions that actually moved money between accounts
        switch (transaction_type) {
          case "Loan Disbursement":
            // This moved real money FROM account TO borrower
            totalBalanceImpact += txAmount;
            if (from_account) {
              accountBalanceChanges[from_account] = (accountBalanceChanges[from_account] || 0) + txAmount;
            }
            break;
            
          case "Loan Repayment":
            // This moved real money FROM borrower TO account  
            totalBalanceImpact += txAmount;
            const repaymentAccount = to_account || account;
            if (repaymentAccount) {
              accountBalanceChanges[repaymentAccount] = (accountBalanceChanges[repaymentAccount] || 0) - txAmount;
            }
            break;
            
          case "Admin Fees":
            // Only reverse if these were actual money movements (not just interest calculations)
            totalBalanceImpact += txAmount;
            if (to_account) {
              accountBalanceChanges[to_account] = (accountBalanceChanges[to_account] || 0) - txAmount;
            }
            break;
            
          case "Transfer Fees":
            // These are actual money movements
            totalBalanceImpact += txAmount;
            if (from_account) {
              accountBalanceChanges[from_account] = (accountBalanceChanges[from_account] || 0) + txAmount;
            }
            break;
            
          // IGNORE interest-only transactions that don't move real money
          case "Interest Payment":
          case "Interest Accrual":
          case "Interest Calculation":
            console.log('🔍 Skipping interest transaction (no real money movement)');
            break;
            
          default:
            // For any other transaction types, include them in balance impact
            totalBalanceImpact += txAmount;
            break;
        }
      }

      return {
        loan,
        relatedData: {
          transactionCount: transactions.length,
          commodityDetailsCount,
          debtCount: debts.length,
          hasCollateral: !!loan.collateral,
          balanceImpact: totalBalanceImpact,
          affectedAccounts: Object.keys(accountBalanceChanges).length,
          accountBalanceChanges
        },
        transactions
      };

    } catch (error) {
      console.error("Error fetching loan deletion preview:", error);
      throw error;
    }
  }
};

export default LoanService;